// `GET /auth/me` — the server's identity surface (`server-entra-auth`, `entra-authentication`).
//
// **Four states, not two** (design **D8**). Three come from the server and one is the client's own
// failure:
//
// - `disabled` — no tenant on this server. Home collects a display name as it always has.
// - `required` — a token is required and this caller has none. The state every player passes
//   through on a first visit, and what tells the root to begin acquiring one.
// - `authenticated` — a verified token, and this is who you are.
// - `unresolved` — the request failed, timed out, or answered with something unusable.
//
// Collapsing `required` into `unresolved` would leave the client unable to distinguish a server
// that wants a sign-in from one it could not reach: the first is actionable and the second is not.
//
// **`unresolved` falls back to the ungated form**, and the asymmetry is what makes that safe. The
// server is the authority: a client that guesses "gated" and omits the name is *refused* by an
// ungated server — a failed join — whereas a client that guesses "ungated" and sends one is simply
// ignored by a gated server. The guess can cost a redundant field, never a failed join.

/** What the composition root publishes to the screens that render an identity. */
export type IdentityState =
  /** This server has no authentication gate. Home collects a display name as it always has. */
  | { kind: 'disabled' }
  /** A token is required and this caller has none. Home offers a sign-in, not a join form. */
  | { kind: 'required' }
  /** A token, verified. The name is the principal's; Home renders it, never edits it. */
  | { kind: 'authenticated'; displayName: string; subject: string }
  /** The request failed, timed out, or the body was unusable. A **definite** state, never a
   * pending one: screens apply their documented ungated fallback rather than waiting on an answer
   * that will not come. */
  | { kind: 'unresolved' };

export interface ResolveIdentityDeps {
  /** The API origin; `''` (the default) is same-origin, which is what production uses. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** How long to wait before publishing `unresolved`. Home paints underneath this either way. */
  timeoutMs?: number;
  /** Injectable only so a test does not spend real seconds on the timeout path. */
  setTimeoutImpl?: (fn: () => void, ms: number) => unknown;
  clearTimeoutImpl?: (handle: unknown) => void;
}

/** Long enough that an ordinary cold start resolves, short enough that a hung request does not
 * leave Home's join form in its fallback shape for the whole session. */
const DEFAULT_TIMEOUT_MS = 5_000;

/**
 * Resolves the server's identity state. **Never throws and never rejects**: every failure mode
 * resolves to `unresolved`, because the caller is a boot-time background publish and an
 * unhandled rejection there would leave every screen waiting forever.
 */
export async function resolveIdentity(deps: ResolveIdentityDeps = {}): Promise<IdentityState> {
  const baseUrl = (deps.baseUrl ?? '').replace(/\/+$/, '');
  const fetchImpl = deps.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const setTimer = deps.setTimeoutImpl ?? ((fn, ms) => globalThis.setTimeout(fn, ms));
  const clearTimer = deps.clearTimeoutImpl ?? ((handle) => globalThis.clearTimeout(handle as never));

  // `AbortController` rather than a `Promise.race` that leaves the request running: a boot-time
  // fetch that nobody is waiting on any more should stop, not linger.
  const controller = new AbortController();
  const timer = setTimer(() => controller.abort(), deps.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  try {
    const response = await fetchImpl(`${baseUrl}/auth/me`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) {
      return { kind: 'unresolved' };
    }
    const body: unknown = await response.json();
    return identityFrom(body);
  } catch {
    // A thrown `fetch`, an abort, or a body that is not JSON. All the same answer: we do not know.
    return { kind: 'unresolved' };
  } finally {
    clearTimer(timer);
  }
}

/** The state a `/auth/me` body names, or `unresolved` when it names none we can use. */
export function identityFrom(body: unknown): IdentityState {
  if (body === null || typeof body !== 'object') {
    return { kind: 'unresolved' };
  }
  const named = body as Record<string, unknown>;
  if (named['authenticated'] !== true) {
    // `required` distinguishes the two `authenticated: false` answers. Anything else is a body we
    // cannot read, and guessing "disabled" from an unreadable body would be the one guess that is
    // not safe.
    if (named['authenticated'] !== false) {
      return { kind: 'unresolved' };
    }
    return named['required'] === true ? { kind: 'required' } : { kind: 'disabled' };
  }
  const displayName = named['displayName'];
  const subject = named['subject'];
  if (typeof displayName !== 'string' || typeof subject !== 'string') {
    return { kind: 'unresolved' };
  }
  return { kind: 'authenticated', displayName, subject };
}
